import { useEffect, useMemo, useRef, useState } from "react";
import { useSnapshot } from "valtio";
import AiGeneratePortraitIcon from "../../components/icons/AiGeneratePortraitIcon";
import ColorSwatchesIcon from "../../components/icons/ColorSwatchesIcon";
import TemplatePreview from "../../components/previews/TemplatePreview";
import TWButton from "../../components/TWButton";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { accessCardState } from "../../stores/accessCardStore";
import { generateDefaultAccessCardHTML } from "../../templates/utils";
import {
  formatDateForPreview,
  formatTimeForPreview,
  getAccessCardDate,
} from "../../utils/dateUtils";
import { editorState } from "../../utils/editor";
import { generateQRCodeSvgDataUri } from "../../utils/qrCodeSvg";
import { getOrderedCoupleFirstNames } from "../../utils/couple.names";
import GenerationStates from "./components/GenerationStates";
import IntroOverlay from "./components/IntroOverlay";
import GeneratePage from "./Generate";
import QRDesignPage, { getContrastRecommendation } from "./QRDesign";
import { AccessCards } from "../../types/wedding";

const AccessCardPage = () => {
  const leftPanelRef = useRef<HTMLDivElement>(null);
  const [showIntro, setShowIntro] = useState(() => {
    try {
      // Check if user has seen the intro before
      const hasSeenIntro = localStorage.getItem("accessCardIntroSeen");
      return !hasSeenIntro;
    } catch (error) {
      // localStorage might be disabled or full
      console.warn("Failed to access localStorage:", error);
      return true; // Show intro by default if localStorage fails
    }
  });

  const isNotMobile = useMediaQuery("(min-width: 768px)");
  const {
    accessCards,
    mainContent,
    eventSchedules,
    brideFirstName,
    groomFirstName,
  } = useSnapshot(editorState);

  const accessCardSnapshot = useSnapshot(accessCardState);
  const {
    activeTab,
    isPreviewOpen,
    selectedEvent,
    selectedEventType,
    generationStatus,
  } = accessCardSnapshot;

  // Get default values from editorState
  const textColor = accessCards?.textColor?.colorCode || "#000000";
  const backgroundColor = accessCards?.backgroundColor?.colorCode || "#FFFFFF";
  const backgroundType = accessCards?.backgroundCtx || "color";
  const contrastRecommendation = getContrastRecommendation(accessCards as unknown as AccessCards);
  const selectedSchedule = eventSchedules?.find((schedule) => schedule._id === selectedEvent);
  const effectiveEventDate = new Date(accessCards?.date || "");
  const hasValidEventDate = !Number.isNaN(effectiveEventDate.getTime()) && effectiveEventDate.getFullYear() >= 2000;
  const eventDate = hasValidEventDate
    ? getAccessCardDate(accessCards?.date, mainContent?.date)
    : selectedSchedule?.date || mainContent?.date;
  const canGenerate = Boolean(selectedEvent && selectedSchedule && hasValidEventDate);
  const formattedDate = formatDateForPreview(eventDate);
  const formattedTime = formatTimeForPreview(eventDate);
  const handleContinue = () => {
    try {
      // Mark intro as seen in localStorage
      localStorage.setItem("accessCardIntroSeen", "true");
    } catch (error) {
      // localStorage might be disabled or full
      console.warn("Failed to save to localStorage:", error);
    }
    setShowIntro(false);
  };

  useEffect(() => {
    if (activeTab === "generate" && !canGenerate) {
      accessCardState.setActiveTab("design");
    }
  }, [activeTab, canGenerate]);

  // Generate QR code SVG as data URI for preview (no actual QR generation needed)
  const qrCodeSvgDataUri = useMemo(() => {
    return generateQRCodeSvgDataUri(textColor);
  }, [textColor]);

  // Generate HTML template with current data
  const [previewHTML, setPreviewHTML] = useState<string>("");

  // Get the selected event's type for the event name
  const eventName = selectedEventType || "Reception";
  const [firstCoupleName = "BRIDE", secondCoupleName = "GROOM"] =
    getOrderedCoupleFirstNames(editorState);

  useEffect(() => {
    let isCurrent = true;
    generateDefaultAccessCardHTML({
      brideName: firstCoupleName,
      groomName: secondCoupleName,
      eventName: eventName,
      showEventName: accessCards?.showEventName ?? true,
      date: formattedDate,
      time: formattedTime,
      venue: accessCards?.address || "",
      guestName: "YOUR GUEST",
      showGuestName: accessCards?.showGuestName ?? false,
      showAdmitsOneOnly: accessCards?.showAdmitsOneOnly ?? false,
      textColor: textColor,
      backgroundColor: backgroundColor,
      backgroundImage: accessCards?.backgroundImage,
      overlayColor: accessCards?.overlayColor?.colorCode || "#000000",
      overlayOpacity: accessCards?.overlayOpacity ?? 0.7,
      backgroundType,
      qrCode: qrCodeSvgDataUri, // Use SVG data URI for preview
      qrPosition: accessCards?.qrPosition || "center",
      qrSize: accessCards?.qrSize || 100,
    }).then((html) => {
      if (isCurrent) setPreviewHTML(html);
    });
    return () => {
      isCurrent = false;
    };
  }, [
    accessCards?.address,
    accessCards?.backgroundImage,
    accessCards?.overlayColor?.colorCode,
    accessCards?.overlayOpacity,
    accessCards?.qrPosition,
    accessCards?.qrSize,
    accessCards?.showGuestName,
    accessCards?.showAdmitsOneOnly,
    accessCards?.showEventName,
    backgroundColor,
    backgroundType,
    brideFirstName,
    eventDate,
    eventName,
    formattedDate,
    formattedTime,
    groomFirstName,
    qrCodeSvgDataUri,
    selectedEvent,
    selectedEventType,
    textColor,
  ]);

  if (showIntro) {
    return <IntroOverlay onContinue={handleContinue} />;
  }

  return (
    <div
      className="h-[calc(100vh-72px)] bg-gray-50"
      style={{ fontFamily: "Glacial, sans-serif" }}
    >
      {/* Main Content */}
      <div className="flex h-full">
        {/* Left Sidebar - Design Controls */}
        <div
          ref={leftPanelRef}
          className="w-full md:w-[52%] lg:w-[55%] xl:w-[56%] md:max-w-[760px] h-full px-4 md:px-6 lg:px-8 bg-white md:border-r border-gray-200 flex flex-col"
        >
          {/* Tab Navigation Header */}
          <div className="px-1 pt-4 pb-5">
            <div className="flex bg-gray-100 rounded-full p-1 border border-gray-200 w-[243px] h-12 mx-auto">
              <TWButton
                onClick={() => accessCardState.setActiveTab("design")}
                variant="tab"
                isActive={activeTab === "design"}
                icon={
                  <ColorSwatchesIcon
                    className="w-4 h-4"
                    strokeColor="#6B7280"
                  />
                }
                className="w-26 h-10"
              >
                DESIGN
              </TWButton>
              <TWButton
                onClick={() => canGenerate && accessCardState.setActiveTab("generate")}
                variant="tab"
                isActive={activeTab === "generate"}
                icon={
                  <AiGeneratePortraitIcon
                    className="w-4 h-4"
                    strokeColor="#6B7280"
                  />
                }
                className="w-30 h-10"
                disabled={!canGenerate}
              >
                GENERATE
              </TWButton>
            </div>
            {!canGenerate && selectedEvent && (
              <p className="mt-2 text-center text-[11px] text-amber-700">
                Complete the event date before generating cards.
              </p>
            )}
          </div>

          {/* Tab Content */}
          <div className="flex-1 overflow-y-auto">
            {activeTab === "design" && <QRDesignPage />}
            {activeTab === "generate" && <GeneratePage />}
          </div>
        </div>

        {/* Right Preview Section - Desktop Only */}
        {isNotMobile &&
          (activeTab === "design" || activeTab === "generate") && (
            <>
              {/* Show generation states (loading/success) if active, otherwise show normal preview */}
              {generationStatus === "loading" ||
              generationStatus === "success" ||
              generationStatus === "error" ? (
                <GenerationStates
                  status={generationStatus}
                  leftPanelRef={leftPanelRef}
                />
              ) : (
                <div className="flex flex-1 min-w-0 p-5 lg:p-8 flex-col items-center justify-center bg-[#F4F5F2] overflow-hidden">
                  <h2 className="font-optima font-normal text-xs text-gray-500 mb-4 uppercase leading-tight tracking-[0.18em]">
                    Live preview
                  </h2>
                  {contrastRecommendation && (
                    <button
                      type="button"
                      onClick={() => {
                        editorState.accessCards.textColor = contrastRecommendation;
                      }}
                      className="mb-4 flex items-center gap-2 rounded-full border border-amber-300 bg-amber-50 px-4 py-1.5 text-xs font-medium text-amber-900 shadow-sm transition hover:bg-amber-100"
                    >
                      <span>⚠️ Low text contrast against background</span>
                      <span className="rounded-full bg-[#172C20] px-2.5 py-0.5 text-[10px] font-semibold uppercase text-white">
                        Use {contrastRecommendation.colorName}
                      </span>
                    </button>
                  )}
                  <div className="h-[min(500px,calc(100vh-180px))] max-w-[92%] aspect-[2/3] overflow-hidden rounded-xl shadow-[0_12px_32px_rgba(23,44,32,0.12)]">
                    <TemplatePreview htmlContent={previewHTML} height="100%" />
                  </div>
                </div>
              )}
            </>
          )}
      </div>

      {!isNotMobile &&
        activeTab === "generate" &&
        generationStatus !== "idle" && (
          <div className="fixed inset-0 z-30 flex bg-gray-50 pt-[72px]">
            <GenerationStates status={generationStatus} />
          </div>
        )}

      {/* Mobile Preview Drawer */}
      {!isNotMobile && isPreviewOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm"
            style={{ zIndex: 40 }}
            onClick={() => accessCardState.setPreviewOpen(false)}
          />

          {/* Drawer */}
          <div
            className="fixed bottom-0 left-0 right-0 bg-white shadow-xl transform transition-transform duration-300 overflow-hidden"
            style={{
              zIndex: 50,
              height: "88vh",
              borderTopLeftRadius: "1rem",
              borderTopRightRadius: "1rem",
            }}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div className="flex-1 text-center">
                <h2 className="font-optima font-normal text-sm text-gray-600 uppercase leading-tight tracking-widest">
                  SAMPLE PREVIEW
                </h2>
              </div>
              <button
                onClick={() => accessCardState.setPreviewOpen(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                aria-label="Close preview"
              >
                <svg
                  className="w-5 h-5 text-gray-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div
              className="flex flex-col items-center justify-center p-4 overflow-hidden bg-[#F4F5F2]"
              style={{ height: "calc(88vh - 73px)" }}
            >
              <div className="w-full max-w-[380px] max-h-full aspect-[2/3] mx-auto overflow-hidden rounded-xl shadow-sm">
                <TemplatePreview htmlContent={previewHTML} height="100%" />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AccessCardPage;
